import { useMemo } from "react";

import {
  deriveConnectionState,
  type ConnectionState,
  type ConnectionStepOutcomes,
  type StepOutcome,
} from "../lib/connection-state";
import { SessionPhase, type SessionPhaseValue } from "../modules/teleop-session";
import type { RobotConfigStatus } from "../modules/teleop-state";
import type { UseAppConfigResult } from "./useAppConfig";
import type { UseRobotResult } from "./useRobot";
import { useReadyTransition, type ReadyTransitionPhase } from "./useReadyTransition";

// Phases reached once the signaling/WebRTC join has gotten far enough for the "connect"
// step to count as done. RECONNECTING is deliberately NOT included, even though it only
// ever occurs after an initial successful connect (see operator-session.ts): a reconnect
// must show the same config→connect→robotConfig→video splash sequence again, with
// "connect" as the current/in-progress step, rather than silently staying on the real
// interface — see `ready` below and `isSessionEstablished`, which each need a different
// answer for RECONNECTING and so are no longer built from the same set.
const CONNECT_DONE_PHASES = new Set<SessionPhaseValue>([SessionPhase.JOINED, SessionPhase.ACTIVE]);

/**
 * Each of these translates ONE step's real, app-specific status into
 * `lib/connection-state.ts`'s generic `StepOutcome` vocabulary. This is where
 * `SessionPhase`/`RobotConfigStatus` knowledge legitimately belongs — kept here, at the
 * UI boundary, precisely so the generic sequencer never has to import either. Each
 * function only looks at the one status it's named for, so a step's rule can be read
 * (and tested) on its own, with no need to hold the other three in your head.
 */

export function configStepOutcome(configStatus: UseAppConfigResult["status"]): StepOutcome {
  if (configStatus === "error") return "error";
  if (configStatus === "ready") return "done";
  return "pending";
}

export function connectStepOutcome(sessionPhase: SessionPhaseValue): StepOutcome {
  if (sessionPhase === SessionPhase.FAILED) return "error";
  if (CONNECT_DONE_PHASES.has(sessionPhase)) return "done";
  return "pending";
}

export function robotConfigStepOutcome(robotStatus: RobotConfigStatus): StepOutcome {
  if (robotStatus === "error") return "error";
  if (robotStatus === "ready") return "done";
  return "pending";
}

/** Joined, but no video producer showed up within the wait window (see
 * `useOperatorSession`'s `videoStalled`) — an empty/producer-less room, most often. */
export function videoStepOutcome(
  sessionPhase: SessionPhaseValue,
  videoStalled: boolean
): StepOutcome {
  if (videoStalled) return "error";
  if (sessionPhase === SessionPhase.ACTIVE) return "done";
  return "pending";
}

/** The slice of session state this gate actually needs — callers pass values they
 * already read via `useStore(session, ...)`, not the store itself. */
export interface ConnectionGateSession {
  phase: SessionPhaseValue;
  videoStalled: boolean;
}

export interface UseConnectionGateResult {
  /** Which step of the primary connect sequence is current/failed — drives
   * `ConnectionSplash`'s step list. See `lib/connection-state.ts`. */
  connectionState: ConnectionState;
  /** Whether to show the real interface instead of the splash. Deliberately NOT
   * `connectionState.kind === "active"` — `CONNECT_DONE_PHASES` flips true as soon as
   * the transport first joins, which can happen before a slow robot-config/video wait
   * window has even had a chance to fail (see teleop-session's NO_VIDEO_PRODUCER, a
   * fire-and-forget 15s timer that doesn't block join). Gating the actual interface on
   * the stricter `connectionState` would hide it in cases it's shown today. */
  ready: boolean;
  phase: ReadyTransitionPhase;
}

/**
 * Single place computing "is this session's connection gate open" — replaces the
 * identical `ready`/`phase` block that used to be duplicated verbatim in ViewerPage and
 * ConsolePage, and the separate step re-derivation `ConnectionSplash` used to do
 * internally from the same underlying inputs.
 */
export function useConnectionGate(
  config: UseAppConfigResult,
  session: ConnectionGateSession,
  robot: UseRobotResult
): UseConnectionGateResult {
  const connectionState = useMemo<ConnectionState>(() => {
    const outcomes: ConnectionStepOutcomes = {
      config: configStepOutcome(config.status),
      connect: connectStepOutcome(session.phase),
      robotConfig: robotConfigStepOutcome(robot.status),
      video: videoStepOutcome(session.phase, session.videoStalled),
    };
    return deriveConnectionState(outcomes);
  }, [config.status, session.phase, robot.status, session.videoStalled]);

  // Splash reopens for RECONNECTING (not in CONNECT_DONE_PHASES) as well as for the
  // initial connect — see that set's own comment.
  const ready = CONNECT_DONE_PHASES.has(session.phase) && robot.ready;
  const phase = useReadyTransition(ready);

  return { connectionState, ready, phase };
}

/**
 * "Is there an established session to send commands on" — true from the first
 * successful join through every reconnect AND a fatal failure (a live `session` object
 * still exists then, only `stop()` clears it), matching what used to be a separate
 * `sessionReady` field before `phase` became authoritative for FAILED too. Exported for
 * callers (camera bootstrap, command gating) that need this without going through the
 * full connection gate.
 *
 * Deliberately broader than `ready`'s own CONNECT_DONE_PHASES: RECONNECTING is a live
 * session mid-recovery (commands may still queue), it just also wants the splash back
 * up — the two questions ("is there a session" vs "should the splash show") diverge
 * exactly on that one phase.
 */
export function isSessionEstablished(phase: SessionPhaseValue): boolean {
  return (
    CONNECT_DONE_PHASES.has(phase) ||
    phase === SessionPhase.RECONNECTING ||
    phase === SessionPhase.FAILED
  );
}
